import { useState } from 'react';

import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { IconPerson } from '@components/icons/person';
import { IconTeam } from '@components/icons/team';
import { hideModal, Modal } from '@components/modal';
import { RadioGroup } from '@components/radio-group/radio-group';
import { Stack } from '@components/stack';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';
import { blurActiveElement } from '@utils/browser';

type ViewSettingsModalProps = {
  defaultName?: string;
  defaultIsCollaborative?: boolean;
  onSave: (name: string, isCollaborative: boolean) => void;
  title?: string;
  saveText?: string;
  isLastCollaborative?: boolean;
  skipChangeRequirement?: boolean;
};

export const ViewSettingsModal = ({
  defaultName = '',
  defaultIsCollaborative = true,
  onSave,
  title,
  saveText,
  isLastCollaborative = false,
  skipChangeRequirement = false,
}: ViewSettingsModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [viewName, setViewName] = useState(defaultName);
  const [viewType, setViewType] = useState<string>(
    defaultIsCollaborative ? 'collaborative' : 'personal'
  );
  const trimmedViewName = viewName.trim();
  const defaultViewType = defaultIsCollaborative ? 'collaborative' : 'personal';
  const hasChanges = trimmedViewName !== defaultName.trim() || viewType !== defaultViewType;

  const handleSave = () => {
    if (!trimmedViewName) return;
    onSave(trimmedViewName, viewType === 'collaborative');
    hideModal();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      blurActiveElement();
      handleSave();
    }
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={title ?? t('viewSettings')} />
      <Modal.Content>
        <Stack space={4}>
          <TextField
            label={t('viewName')}
            placeholder={t('placeholder')}
            value={viewName}
            onChange={setViewName}
            onKeyDown={handleKeyDown}
            maxChars={255}
            autoFocus
          />
          <RadioGroup
            label={t('access')}
            value={viewType}
            onChange={(value) => setViewType(value as string)}
            options={[
              {
                value: 'collaborative',
                label: t('collaborative'),
                icon: <IconTeam />,
                helpText: t('collaborativeDescription'),
              },
              {
                value: 'personal',
                label: t('personal'),
                icon: <IconPerson />,
                helpText: t('personalDescription'),
                isDisabled: isLastCollaborative,
                tooltip: isLastCollaborative ? t('cannotSwitchToPersonal') : undefined,
              },
            ]}
          />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <CancelSaveButtons
          alwaysShow
          onCancel={hideModal}
          onSave={handleSave}
          isSaveDisabled={!trimmedViewName || (!skipChangeRequirement && !hasChanges)}
          {...(saveText && { translationsOverride: { save: saveText } })}
        />
      </Modal.Footer>
    </Modal>
  );
};

const locale = {
  en: {
    viewSettings: 'View settings',
    viewName: 'View name',
    placeholder: 'Name your view...',
    access: 'Access (View & edit)',
    collaborative: 'Collaborative',
    collaborativeDescription: '(Anyone in workspace)',
    personal: 'Personal',
    personalDescription: '(Only you)',
    cannotSwitchToPersonal:
      'This is the only collaborative view. Create another shared view before switching this to personal.',
  },
  is: {
    viewSettings: 'Stillingar ásýndar',
    viewName: 'Nafn ásýndar',
    placeholder: 'Nafn ásýndarinnar...',
    access: 'Aðgangur (Skoða og breyta)',
    collaborative: 'Sameiginleg',
    collaborativeDescription: '(Allir á vinnusvæði)',
    personal: 'Persónuleg',
    personalDescription: '(Aðeins þú)',
    cannotSwitchToPersonal:
      'Þetta er eina sameiginlega ásýndin. Búðu til aðra sameiginlega ásýnd áður en þú breytir þessari í persónulega.',
  },
};
